import { type CSSProperties, useEffect, useId, useRef, useState } from 'react'
import cx from 'classnames'
import {
  clamp,
  m,
  type TargetAndTransition,
  type Transition,
  useReducedMotion,
} from 'motion/react'

import styles from './EdgeGlow.module.css'

const PATH_SAMPLE_COUNT = 128
const MASK_REVEAL_DURATION = 0.4
const EXIT_FADE_DURATION = 0.2

interface MaskKeyframes {
  x: number[]
  y: number[]
  rotate: number[]
}

const initialMaskKeyframes: MaskKeyframes = {
  x: [0, 0],
  y: [0, 0],
  rotate: [0, 0],
}

const pulseTransition: Transition = {
  duration: 3.6,
  ease: 'easeInOut',
  repeat: Infinity,
}

export interface EdgeGlowProps {
  /**
   * Color of the soft glow that fades toward the center.
   */
  glowColor?: string

  /**
   * Distance the soft glow extends inward from the edge. Supports container
   * units such as `cqh` for sizing relative to the container height.
   */
  glowDepth?: string

  /**
   * Total length of the soft glow along the edge. Supports container units
   * such as `cqw` for sizing relative to the container width.
   */
  glowLength?: string

  /**
   * Primary color of the border glint.
   */
  borderColor?: string

  /**
   * Brightest color at the center of the border glint.
   */
  borderHighlightColor?: string

  /**
   * Thickness of the border glint.
   */
  borderWidth?: string

  /**
   * Total fade length of the border glint along the edge. Supports container
   * units such as `cqw` for sizing relative to the container width.
   */
  glintLength?: string

  /**
   * Length of the bright center within the border glint. Supports container
   * units such as `cqw` for sizing relative to the container width.
   */
  highlightLength?: string

  /**
   * Border radius of the glow. Defaults to square corners. Pass `inherit` or
   * the same radius token used by the parent to match its corners.
   */
  borderRadius?: string

  /**
   * Duration of one complete rotation, in seconds.
   */
  duration?: number

  /**
   * Progress around the track where the effect begins, from `0` to `1`.
   */
  startOffset?: number

  /**
   * Motion easing used across one complete rotation.
   */
  easing?: Transition['ease']
}

/**
 * A rotating soft glow and border glint for a positioned container.
 * Pass `borderRadius="inherit"` or the parent's radius token when the glow
 * should follow rounded corners.
 */
export function EdgeGlow({
  glowColor,
  glowDepth,
  glowLength,
  borderColor,
  borderHighlightColor,
  borderWidth,
  glintLength,
  highlightLength,
  borderRadius,
  duration = 3,
  startOffset,
  easing = 'linear',
}: EdgeGlowProps) {
  const id = useId().replaceAll(':', '')
  const glowMaskId = `edge-glow-mask-${id}`
  const glintMaskId = `edge-glow-glint-mask-${id}`
  const highlightMaskId = `edge-glow-highlight-mask-${id}`
  const revealMaskGradientId = `edge-glow-reveal-mask-gradient-${id}`
  const trackRef = useRef<SVGRectElement>(null)
  const [maskKeyframes, setMaskKeyframes] = useState(initialMaskKeyframes)
  const shouldReduceMotion = useReducedMotion()

  useEffect(() => {
    const track = trackRef.current
    if (!track) {
      return
    }

    // Measures the SVG track and samples position and tangent-angle keyframes.
    const measureTrack = () => {
      const trackLength = track.getTotalLength()

      // Keyframes for the mask's position and rotation.
      const x: number[] = []
      const y: number[] = []
      const rotate: number[] = []

      // Used for unwrapping the angle (e.g. 190deg instead of -170deg) for
      // animation.
      let previousAngle = 0

      // Calculate the distance along the track from where to start.
      const clampedStartOffset = clamp(0, 1, startOffset ?? 0)
      const startDistance = clampedStartOffset * trackLength

      // Keyframes are created by sampling points along the track.
      for (let index = 0; index <= PATH_SAMPLE_COUNT; index += 1) {
        // Distance along the track for this keyframe.
        const distance =
          (startDistance + (index / PATH_SAMPLE_COUNT) * trackLength) %
          trackLength

        // Gets the coordinate point in SVG coordinate space.
        const point = track.getPointAtLength(distance)

        // Sample a point slightly farther along the track to determine
        // its direction.
        const nextPoint = track.getPointAtLength((distance + 0.5) % trackLength)

        // Calculate the track's tangent angle so the mask points in the
        // direction of travel.
        let angle =
          (Math.atan2(nextPoint.y - point.y, nextPoint.x - point.x) * 180) /
          Math.PI

        // If the new angle looks more than half a turn ahead,
        // subtract full turns until it is nearby.
        while (angle - previousAngle > 180) {
          angle -= 360
        }

        // If the new angle looks more than half a turn behind,
        // add full turns until it is nearby.
        while (angle - previousAngle < -180) {
          angle += 360
        }

        x.push(point.x)
        y.push(point.y)
        rotate.push(angle)
        previousAngle = angle
      }

      setMaskKeyframes({ x, y, rotate })
    }

    measureTrack()
    const observer = new ResizeObserver(measureTrack)
    observer.observe(track.ownerSVGElement!)

    return () => observer.disconnect()
  }, [startOffset])

  const maskAnimation: TargetAndTransition = shouldReduceMotion
    ? {
        x: maskKeyframes.x[0],
        y: maskKeyframes.y[0],
        rotate: maskKeyframes.rotate[0],
      }
    : {
        x: maskKeyframes.x,
        y: maskKeyframes.y,
        rotate: maskKeyframes.rotate,
      }
  const maskTransition: Transition = {
    duration,
    ease: easing,
    repeat: Infinity,
  }

  // When exiting, a circle expands which causes the mask to progressively
  // reveal the glow and border before fading out.
  const maskReveal = (
    <m.circle
      fill={`url(#${revealMaskGradientId})`}
      initial={{ r: '0%' }}
      animate={{ ...maskAnimation, r: '0%' }}
      transition={maskTransition}
      exit={{
        r: '250%',
        transition: {
          duration: shouldReduceMotion ? 0 : MASK_REVEAL_DURATION,
          ease: 'easeOut',
        },
      }}
    />
  )

  const style = {
    '--edge-glow-color': glowColor,
    '--edge-glow-depth': glowDepth,
    '--edge-glow-length': glowLength,
    '--edge-glow-border-color': borderColor,
    '--edge-glow-border-highlight-color': borderHighlightColor,
    '--edge-glow-border-width': borderWidth,
    '--edge-glow-glint-length': glintLength,
    '--edge-glow-highlight-length': highlightLength,
    '--edge-glow-border-radius': borderRadius,
  } as CSSProperties

  return (
    <m.div
      aria-hidden="true"
      className={cx(styles.root)}
      style={style}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{
        opacity: 0,
        transition: shouldReduceMotion
          ? { duration: 0 }
          : {
              delay: MASK_REVEAL_DURATION,
              duration: EXIT_FADE_DURATION,
              ease: 'easeOut',
            },
      }}
    >
      <svg className={styles.svg} focusable="false">
        <defs>
          <radialGradient id={`${glowMaskId}-gradient`}>
            <stop offset="0%" stopColor="white" />
            <stop offset="45%" stopColor="white" stopOpacity="0.8" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <radialGradient id={`${highlightMaskId}-gradient`}>
            <stop offset="0%" stopColor="white" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <radialGradient id={revealMaskGradientId}>
            <stop offset="0%" stopColor="white" />
            <stop offset="80%" stopColor="white" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <mask id={glowMaskId} maskUnits="userSpaceOnUse">
            <m.ellipse
              className={cx(styles.glowMask)}
              fill={`url(#${glowMaskId}-gradient)`}
              animate={maskAnimation}
              transition={maskTransition}
            />
            {maskReveal}
          </mask>
          <mask id={glintMaskId} maskUnits="userSpaceOnUse">
            <m.ellipse
              className={cx(styles.glintMask)}
              fill={`url(#${glowMaskId}-gradient)`}
              animate={maskAnimation}
              transition={maskTransition}
            />
            {maskReveal}
          </mask>
          <mask id={highlightMaskId} maskUnits="userSpaceOnUse">
            <m.ellipse
              className={cx(styles.highlightMask)}
              fill={`url(#${highlightMaskId}-gradient)`}
              animate={maskAnimation}
              transition={maskTransition}
            />
            {maskReveal}
          </mask>
        </defs>
        <m.rect
          ref={trackRef}
          className={cx(styles.track, styles.softGlow)}
          mask={shouldReduceMotion ? undefined : `url(#${glowMaskId})`}
          transition={pulseTransition}
          animate={
            shouldReduceMotion ? { opacity: [0.45, 0.8, 0.45] } : { opacity: 1 }
          }
        />
        <m.rect
          className={cx(styles.track, styles.borderGlint)}
          mask={shouldReduceMotion ? undefined : `url(#${glintMaskId})`}
          transition={pulseTransition}
          animate={
            shouldReduceMotion ? { opacity: [0.65, 1, 0.65] } : { opacity: 1 }
          }
        />
        {!shouldReduceMotion && (
          <rect
            className={cx(styles.track, styles.borderHighlight)}
            mask={`url(#${highlightMaskId})`}
          />
        )}
      </svg>
    </m.div>
  )
}
